import { useState } from 'react';
import { Controller } from 'react-hook-form-v7';
import { useTranslation } from 'react-i18next';
import { Card, Tabs, TabsList, TabsTrigger, TabsContent } from '@parrot-software/design-system';

import { RoleFormProps, RoleFormData } from '@personnel/presentation/types';
import type { FrontendPermissionId } from '@personnel/domain';

import { PermissionsRenderer } from '@personnel/presentation/components/permissions/PermissionsRenderer';
import { FormTextField } from '@personnel/presentation/components/shared/FormTextField';

/**
 * Role Form Component
 *
 * Main form component for creating, editing, and duplicating roles.
 * Receives form methods from parent for centralized state management.
 * Uses Semantic Styles Pattern for maintainability.
 *
 * @param {RoleFormProps} props - Component properties
 * @returns {React.ReactElement} Role form component
 *
 * @example
 * const methods = useFormV7<RoleFormData>({ ... });
 * <RoleForm
 *   methods={methods}
 *   onSubmit={handleSubmit}
 *   submitLabel="Crear rol"
 * />
 */
const styles = {
  form: 'flex flex-col gap-6',
  inputGroup: 'p-6',
  inputGroupContent: 'max-w-[320px]',
  errorMessage: 'text-sm text-destructive',
  tabsList: 'bg-muted p-1.5 rounded-md inline-flex w-fit h-auto',
  tabsTrigger:
    'px-3 py-1.5 text-sm font-medium rounded-xs data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-xs',
  tabsContent: 'mt-4',
  hiddenButton: 'hidden',
};

export const RoleForm = ({
  methods,
  onSubmit,
  submitLabel,
  isSubmitting = false,
  disabled = false,
}: RoleFormProps): React.ReactElement => {
  const { t } = useTranslation('roles');
  const [activeTab, setActiveTab] = useState<'organization' | 'store'>('organization');

  const handleSubmit = methods.handleSubmit(async (data: RoleFormData) => {
    await onSubmit(data);
  });

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      {/* Role Name Input */}
      <Card className={styles.inputGroup}>
        <div className={styles.inputGroupContent}>
          <Controller
            control={methods.control}
            name="name"
            render={({ field, fieldState }) => (
              <FormTextField
                disabled={disabled}
                error={fieldState.error?.message}
                field={field}
                id="role-name"
                label={t('form.nameLabel')}
                maxLength={255}
                placeholder={t('form.namePlaceholder')}
                required
              />
            )}
          />
        </div>
      </Card>

      {/* Tabs: Organization / Store */}
      <Tabs
        onValueChange={(value) => {
          const scope = value as 'organization' | 'store';
          setActiveTab(scope);
          methods.setValue('scope', scope);
        }}
        value={activeTab}
      >
        <TabsList className={styles.tabsList}>
          <TabsTrigger className={styles.tabsTrigger} value="organization">
            {t('form.organizationTab')}
          </TabsTrigger>
          <TabsTrigger className={styles.tabsTrigger} value="store">
            {t('form.storeTab')}
          </TabsTrigger>
        </TabsList>

        {/* Permissions Renderer - Organization */}
        <TabsContent className={styles.tabsContent} value="organization">
          <Controller
            control={methods.control}
            name="permissions"
            render={({ field }) => (
              <PermissionsRenderer
                disabled={disabled}
                level="organization"
                onChange={(permissions: FrontendPermissionId[]) => field.onChange(permissions)}
                selectedPermissions={field.value || []}
              />
            )}
          />
        </TabsContent>

        {/* Permissions Renderer - Store */}
        <TabsContent className={styles.tabsContent} value="store">
          <Controller
            control={methods.control}
            name="permissions"
            render={({ field }) => (
              <PermissionsRenderer
                disabled={disabled}
                level="store"
                onChange={(permissions: FrontendPermissionId[]) => field.onChange(permissions)}
                selectedPermissions={field.value || []}
              />
            )}
          />
        </TabsContent>

        {/* Error Message */}
        {methods.formState.errors.permissions && (
          <p className={styles.errorMessage}>{methods.formState.errors.permissions.message}</p>
        )}
      </Tabs>

      {/* Hidden submit button - triggered by PageHeader button */}
      <button className={styles.hiddenButton} disabled={isSubmitting} id="role-form-submit" type="submit">
        {submitLabel}
      </button>
    </form>
  );
};
